import { HIPAA_DEF, HIPAA_SECTIONS, FieldDef } from "../config/hipaa";

export type Group = { title?: string; fields: string[] };

export function buildGroups(formValues: Record<string, any>): Group[] {
  const g: Group[] = [];
  g.push({ fields: ["patient_full_name", "patient_dob"] });
  g.push({ title: "Contact Information", fields: ["patient_email", "patient_phone"] });
  g.push({ title: "Patient Address", fields: [
    "patient_address_line1",
    "patient_address_line2",
    "patient_address_city",
    "patient_address_state",
    "patient_address_zip",
  ] });
  g.push({ fields: ["mrn"] });
  g.push({ fields: ["provider_name"] });
  g.push({ title: "Provider Address", fields: [
    "provider_address_line1",
    "provider_address_line2",
    "provider_address_city",
    "provider_address_state",
    "provider_address_zip",
  ] });
  g.push({ fields: ["recipient_name", "recipient_org"] });
  g.push({ fields: [
    "purpose_treatment",
    "purpose_insurance",
    "purpose_legal",
    "purpose_personal",
    "purpose_other",
    ...(formValues.purpose_other ? ["purpose_other_text"] : []),
  ] });
  g.push({ fields: ["dates_all", ...(formValues.dates_all ? [] : ["date_from", "date_to"]) ] });
  g.push({ fields: [
    "info_all",
    ...(formValues.info_all ? [] : [
      "info_office_visits",
      "info_lab_results",
      "info_imaging",
      "info_immunizations",
      "info_billing",
      "info_other",
      ...(formValues.info_other ? ["info_other_text"] : []),
    ]),
  ] });
  g.push({ fields: [
    "sensitive_mental_health",
    "sensitive_hiv_aids",
    "sensitive_genetic",
    "sensitive_substance_use",
    ...(formValues.sensitive_mental_health || formValues.sensitive_hiv_aids || formValues.sensitive_genetic || formValues.sensitive_substance_use ? ["sensitive_initials"] : []),
  ] });
  g.push({ fields: ["expiration_date", "expiration_event"] });
  g.push({ fields: ["ack_revocation", "ack_redisclosure", "ack_electronic_delivery", "ack_conditions"] });
  g.push({ fields: [
    "representative_is_patient",
    "representative_is_guardian",
    ...(formValues.representative_is_guardian ? ["representative_relationship"] : []),
  ] });
  return g;
}

export function isFieldValid(def: FieldDef, values: Record<string, any>) {
  const value = values[def.name];
  const isReq = ("required" in (def as any) && (def as any).required === true) ||
    (
      (def.name === "purpose_other_text" && values.purpose_other) ||
      ((def.name === "date_from" || def.name === "date_to") && !values.dates_all) ||
      (def.name === "sensitive_initials" && (
        values.sensitive_mental_health ||
        values.sensitive_hiv_aids ||
        values.sensitive_genetic ||
        values.sensitive_substance_use
      )) ||
      (def.name === "representative_relationship" && values.representative_is_guardian)
    );
  if (!isReq) return true;
  if (def.type === "checkbox") return Boolean(value);
  if (def.type === "email") return /.+@.+\..+/.test(String(value || ""));
  return String(value || "").trim().length > 0;
}

export function computeSectionStatuses(values: Record<string, any>) {
  return HIPAA_SECTIONS.map((s) => {
    const fields = (HIPAA_DEF.fields as readonly FieldDef[]).filter((f) => f.sectionId === s.id);
    const requiredFields = fields.filter((f) => {
      const baseReq = ("required" in (f as any) && (f as any).required === true);
      const dynReq = (
        (f.name === "purpose_other_text" && values.purpose_other) ||
        ((f.name === "date_from" || f.name === "date_to") && !values.dates_all) ||
        (f.name === "sensitive_initials" && (
          values.sensitive_mental_health ||
          values.sensitive_hiv_aids ||
          values.sensitive_genetic ||
          values.sensitive_substance_use
        )) ||
        (f.name === "representative_relationship" && values.representative_is_guardian)
      );
      return baseReq || dynReq;
    });
    const remaining = requiredFields.filter((f) => !isFieldValid(f, values)).length;
    return { id: s.id, title: s.title, completed: remaining === 0, remaining, totalRequired: requiredFields.length };
  });
}

